/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * INT-02 — Order-summary "PROTECTION PLAN(S)" breakdown line.
 *
 * Mirrors the legacy SFRA cart summary block (`ProtectionPlanTotalLine` in
 * the Ashley-PP code path). The legacy block:
 *
 *   PROTECTION PLAN(S)
 *      Indoor Furniture                                  $349.99
 *
 * is now driven entirely by Extend basket enrichment:
 *
 *   - The `basket.modifyGETResponse` hook (`ExtendBasketEnricher`) sets
 *     `c_isProtectionPlan = true` on warranty PLIs, mirrors the line cost
 *     onto `c_ppCost` (currency main unit — dollars for USD; same unit as
 *     the legacy Reguard `c_ppCost` so consumers don't need to branch on
 *     PP vendor), and stamps `c_ppTypeName` from the warranty catalog
 *     product's `productName` (e.g. "Indoor Furniture").
 *   - The PDP add-to-cart flow upserts warranty PLIs via the cartridge
 *     reconciliation (`ExtendReconciliation`) when the shopper keeps the
 *     PDP banner selected; the cart-row toggle flips the same selection.
 *   - This component groups warranty PLIs by `c_ppTypeName` and sums the
 *     per-PLI warranty cost so multiple covered SKUs of the same plan
 *     type roll into one row. Cost resolution mirrors the resilient
 *     fallback chain in `getProtectionPlanTotal` /
 *     `_app.order-confirmation.$orderNo` (see `readWarrantyLineCost`
 *     below) — `c_ppCost` when the enricher ran, else the standard SCAPI
 *     line-price fields, else the sum of `priceAdjustments`. Without
 *     that chain a downstream basket-response path that arrives without
 *     `c_ppCost` (e.g. checkout review post-mutation, or before the
 *     enricher runs) renders each row at $0.00 even though the total
 *     row correctly reflects the plan cost.
 *
 * Plan-details link — the legacy SFRA block rendered each label as an
 * `<a class="extend-protection-summary-link" target="_blank" href="…">`
 * pointing at the Extend plan-details page. Per the merchandiser ask
 * (Jun 2026), that anchor moves up to the cart-row Extend toggle's
 * "LEARN MORE" CTA, which now opens the same URL (sourced from the live
 * offer's `marketing.learnMoreModal.planDetailsUrl`). This summary row
 * is purely informational — plain text, no anchor.
 *
 * Renders `null` when no warranty PLIs are present so the order summary
 * doesn't get an empty heading.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/currency';
import { useOrderSummaryContext } from './context';

interface WarrantyProductItemExtras {
    c_isProtectionPlan?: boolean;
    c_ppCost?: number;
    c_ppTypeName?: string | null;
    priceAfterOrderDiscount?: number;
    priceAfterItemDiscount?: number;
    price?: number;
    basePrice?: number;
    priceAdjustments?: Array<{ price?: number }>;
}

/**
 * SKU → merchandiser-facing plan label. Mirrors the cartridge-side map in
 * `int_sfnext_extend/scripts/extend/ExtendDefaults.js` (`PLAN_DISPLAY_LABELS`
 * / `getPlanDisplayLabel`). On the cart page the basket enricher populates
 * `c_ppTypeName` from that map, so this table is a no-op. On the checkout
 * page some downstream basket-response paths arrive without the enricher's
 * `c_ppTypeName` — the component would then fall through to the raw
 * catalog `productName` (e.g. "Indoor Furniture Protection Plan by
 * Extend"), making the label disagree with the cart. Applying the same
 * SKU→label lookup client-side keeps both surfaces in lockstep.
 *
 * Keep this table in sync with the cartridge map — the SKUs listed here
 * are the authoritative Ashley warranty SKUs.
 */
const PLAN_DISPLAY_LABELS: Readonly<Record<string, string>> = Object.freeze({
    'FURADJ-10Y': 'Standard Adjustable Bases',
    'FURADJK-10Y': 'King Adjustable Bases',
    'FURNID-5Y': 'Indoor Furniture',
    'FURNOD-5Y': 'Outdoor Furniture',
    'FURMATT-10Y': 'Mattresses',
    'FURUGS-5Y': 'Rugs',
});

interface PlanGroup {
    /** Stable key — the warranty PLI's SFCC SKU (Ashley warranty SKU,
     *  e.g. `FURADJ-10Y`) so structurally distinct plans stay separate. */
    key: string;
    /** Uppercase-rendered plan label. */
    label: string;
    /** Sum of warranty PLI costs in the basket currency's main unit
     *  (dollars for USD). Matches what `formatCurrency` expects. */
    total: number;
}

/**
 * Resolve the per-line warranty cost with the same resilience as
 * `getProtectionPlanTotal` + the order-confirmation route. On the cart page
 * the Extend basket enricher stamps `c_ppCost` so the fast path is a single
 * field read, but on some downstream basket responses (checkout review
 * post-mutation, or when the enricher couldn't run) that field is absent
 * even though the warranty cost lives on the PLI as one of:
 *   - `priceAfterOrderDiscount` / `priceAfterItemDiscount` — SFCC's
 *     canonical line-price fields once discounts settle.
 *   - `price` / `basePrice` — raw catalog fields (usually $0 for a
 *     warranty PLI but included as a last-resort direct read).
 *   - `priceAdjustments[].price` — the actual source of truth in the
 *     Extend flow: reconciliation builds warranty PLIs at `priceValue=0`
 *     plus a single positive `PriceAdjustment` carrying the plan cost.
 *
 * Without this fallback the individual "Protection Plan(s)" rows render
 * $0.00 whenever the response arrives without `c_ppCost` — the summary's
 * total continues to be correct because it reads `basket.c_totalPPCost`
 * (or falls back to this same set of fields), but the per-line breakdown
 * silently disagreed.
 */
function readWarrantyLineCost(extras: WarrantyProductItemExtras): number {
    const readFiniteAmount = (value: unknown): number | undefined =>
        typeof value === 'number' && Number.isFinite(value) ? value : undefined;

    const directCost = [
        extras.c_ppCost,
        extras.priceAfterOrderDiscount,
        extras.priceAfterItemDiscount,
        extras.price,
        extras.basePrice,
    ]
        .map(readFiniteAmount)
        .find((value): value is number => value !== undefined);

    if (directCost !== undefined && directCost !== 0) return directCost;

    const adjustmentTotal = (extras.priceAdjustments ?? []).reduce((sum, adjustment) => {
        const value = readFiniteAmount(adjustment.price);
        return value !== undefined ? sum + value : sum;
    }, 0);
    if (Number.isFinite(adjustmentTotal) && adjustmentTotal !== 0) return adjustmentTotal;

    return directCost ?? 0;
}

export function ProtectionPlanTotalLine(): ReactElement | null {
    const { t, i18n } = useTranslation('cart');
    const { basket, currency } = useOrderSummaryContext();

    const productItems = basket.productItems ?? [];
    const warrantyItems = productItems.filter(
        (item) => (item as WarrantyProductItemExtras).c_isProtectionPlan === true
    );

    if (warrantyItems.length === 0) return null;

    // Group by the warranty PLI's SFCC SKU (Ashley warranty SKU, e.g.
    // `FURADJ-10Y`, `FURADJK-10Y`). Extend's OBO API can return the same
    // `extendCategory` label for structurally different plans (Standard vs
    // King adjustable base both come back as `Big Box Retailers Misc`), so
    // the API label is not a stable grouping key. The SKU is guaranteed
    // unique per plan type by cartridge reconciliation.
    const groupsByKey = new Map<string, PlanGroup>();
    for (const item of warrantyItems) {
        const extras = item as WarrantyProductItemExtras;
        const productId = (item.productId ?? '').trim();
        if (!productId) continue;
        // Label resolution order — mirrors the cartridge enricher's
        // `mappedLabel || extendCategory || productName || null` chain so
        // cart and checkout stay in sync. Client-side SKU map runs before
        // `productName` so that a response missing `c_ppTypeName` (checkout
        // review after mutation) still renders the short merchandiser label
        // instead of the long catalog "…Protection Plan by Extend" name.
        const enricherLabel = extras.c_ppTypeName && extras.c_ppTypeName.trim();
        const mappedLabel = PLAN_DISPLAY_LABELS[productId];
        const productNameLabel = (item.productName ?? '').trim();
        const rawLabel = enricherLabel || mappedLabel || productNameLabel || productId;
        const existing = groupsByKey.get(productId);
        const cost = readWarrantyLineCost(extras);
        if (existing) {
            existing.total += cost;
        } else {
            groupsByKey.set(productId, { key: productId, label: rawLabel, total: cost });
        }
    }

    const groups = Array.from(groupsByKey.values());
    if (groups.length === 0) return null;

    return (
        <div data-testid="order-summary-protection-plans" className="border-t border-input py-3">
            <p className="text-xs font-semibold leading-4 text-foreground">{t('summary.protectionPlansHeading')}</p>
            <div className="mt-1">
                {groups.map((group) => (
                    <div
                        key={group.key}
                        data-testid="order-summary-protection-plan-line"
                        className="flex items-center justify-between py-1">
                        <span className="text-sm leading-5 text-primary">{group.label}</span>
                        <span className="text-sm leading-5 text-foreground">
                            {formatCurrency(group.total, i18n.language, currency)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default ProtectionPlanTotalLine;
